import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Session, User, FunctionsHttpError, FunctionsRelayError, FunctionsFetchError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabaseClient';
import { AuthErrorInterceptor, type AuthErrorDetails } from '@/lib/auth-error-interceptor';
import { loadUserLanguageAfterLogin, changeLanguage, detectUserLanguage } from '@/lib/language-service';
import { authLogger } from '@/lib/logging';
import { getGlobalRefreshPromise, setGlobalRefreshPromise, markSessionFresh } from '@/lib/auth-utils';
import { checkStorageHealth, clearAuthStorage, clearAllStorage } from '@/lib/storage-health';

export type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isPasswordRecovery: boolean;
  handleAuthError: (_error: Error | unknown) => void;
  authErrorDetails: AuthErrorDetails | null;
  clearAuthErrorDetails: () => void;
  refreshToken: () => Promise<{ success: boolean; error?: string }>;
  /** Whether storage corruption was detected and silent recovery failed */
  storageCorrupted: boolean;
  /** Attempt to recover from storage corruption (nuclear option - clears everything) */
  attemptStorageRecovery: () => Promise<boolean>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [authErrorDetails, setAuthErrorDetails] = useState<AuthErrorDetails | null>(null);
  const [storageCorrupted, setStorageCorrupted] = useState(false);

  // CRITICAL FIX: Use useRef instead of useState to prevent state persistence issues
  const isUserInitiatedSignOut = React.useRef(false);

  // Track when SIGNED_IN was last processed (for spurious SIGNED_OUT detection without getSession lock)
  const lastSignedInTime = React.useRef<number>(0);

  // Mutex refs for proactive refresh coordination
  const refreshMutex = React.useRef<Promise<void> | null>(null);
  const lastRefreshCheck = React.useRef<number>(0);

  // Timeout helper for session operations
  const withTimeout = <T,>(
    promise: Promise<T>,
    timeoutMs: number,
    operation: string
  ): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(
          () => reject(new Error(`${operation} timed out after ${timeoutMs}ms`)),
          timeoutMs
        )
      ),
    ]);
  };

  // Clear auth state function for error handler
  // CRITICAL: Do NOT call supabase.auth.signOut() here!
  // signOut() acquires the navigator lock (exclusive, infinite timeout) and makes a
  // network call to /auth/v1/logout. If the network is slow (deployment, cold start),
  // the lock is held for seconds/minutes. ALL getSession() calls in the app — including
  // internal ones from supabase.functions.invoke() — queue behind this held lock.
  // Our 3s withTimeout moves on, but signOut() keeps holding the lock in the background.
  // This causes 24+ pending lock requests and a complete app hang.
  // Instead, we clear localStorage directly — no lock, no network call.
  const clearAuthState = useCallback(async () => {
    authLogger.warn('Clearing auth state (localStorage-only, no signOut lock)');
    setSession(null);
    setUser(null);
    setIsPasswordRecovery(false);

    try {
      const supabaseUrl = import.meta.env['VITE_PUBLIC_SUPABASE_URL'] as string | undefined;
      const projectRef = supabaseUrl?.split('//')[1]?.split('.')[0];
      if (projectRef) {
        const storageKey = `sb-${projectRef}-auth-token`;
        localStorage.removeItem(storageKey);
        localStorage.removeItem(`${storageKey}-code-verifier`);
      }
      authLogger.warn('Auth tokens cleared from localStorage');
    } catch (_error) {
      authLogger.error('Failed to clear auth tokens from localStorage', _error);
    }
  }, []); // Empty deps - only uses stable setState functions

  // Redirect to login function for error handler
  const redirectToLogin = useCallback(() => {
    authLogger.warn('Redirecting to login due to auth error');
    window.history.replaceState({}, '', window.location.pathname);
    window.location.href = '/login';
  }, []); // Empty deps - only uses window API and logger

  // Enhanced global auth error handler
  const handleAuthError = useCallback(async (error: Error | unknown) => {
    if (AuthErrorInterceptor.shouldIgnoreError(error)) {
      return;
    }

    // CRITICAL: Handle token invalidation errors (from user profile updates)
    const errorCode = (error as { code?: string }).code;
    const requiresTokenRefresh = (error as { context?: { requiresTokenRefresh?: boolean } }).context?.requiresTokenRefresh;

    if (errorCode === 'AUTH_TOKEN_INVALID' ||
        errorCode === 'AUTH_TOKEN_EXPIRED' ||
        requiresTokenRefresh) {
      authLogger.warn('Token invalidation detected - attempting automatic refresh', {
        errorCode,
        requiresTokenRefresh,
        message: (error as Error).message
      });

      try {
        // Attempt to refresh the session
        const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshedSession && !refreshError) {
          authLogger.warn('✅ Token refresh successful after invalidation');
          setSession(refreshedSession);
          setUser(refreshedSession.user);

          // Clear any error details since we recovered
          setAuthErrorDetails(null);
          return;
        } else {
          authLogger.error('Token refresh failed', refreshError);
          // Fall through to normal error handling
        }
      } catch (refreshError) {
        authLogger.error('Token refresh threw exception', refreshError);
        // Fall through to normal error handling
      }
    }

    // Special handling for deployment-related race condition errors
    if (errorCode === 'AUTH_DEPLOYMENT_RACE_CONDITION' ||
        errorCode === 'DEPLOYMENT_RACE_CONDITION_EXHAUSTED') {
      authLogger.warn('Deployment race condition detected', (error as Error).message);

      if (errorCode === 'AUTH_DEPLOYMENT_RACE_CONDITION') {
        authLogger.warn('Attempting session recovery for deployment race condition');
        try {
          const { data: { session: recoveredSession } } = await supabase.auth.getSession();
          if (recoveredSession) {
            authLogger.warn('Session recovery successful after deployment');
            setSession(recoveredSession);
            setUser(recoveredSession.user);
            return;
          }
        } catch (recoveryError) {
          authLogger.error('Session recovery failed', recoveryError);
        }
      }

      setAuthErrorDetails({
        type: 'network_error',
        originalError: error,
        shouldRedirectToLogin: false,
        shouldClearSession: false,
        userMessage: (error as { deploymentRelated?: boolean }).deploymentRelated
          ? 'System is warming up after deployment. Please wait a moment and try again.'
          : 'Authentication service is temporarily overloaded. Please try again shortly.'
      });
      return;
    }

    let errorDetails = await AuthErrorInterceptor.analyzeSupabaseError(error);
    if (!errorDetails) {
      errorDetails = AuthErrorInterceptor.analyzeError(error);
    }

    if (errorDetails) {
      authLogger.error('Auth error detected', error, {
        type: errorDetails.type,
        isEdgeRuntimeRejection: error instanceof FunctionsFetchError,
        isHttpError: error instanceof FunctionsHttpError,
        isNetworkError: error instanceof FunctionsRelayError,
        shouldRedirect: errorDetails.shouldRedirectToLogin,
        shouldClear: errorDetails.shouldClearSession,
        isDeploymentRelated: (error as { deploymentRelated?: boolean }).deploymentRelated || false,
        timestamp: new Date().toISOString()
      });

      setAuthErrorDetails(errorDetails);

      await AuthErrorInterceptor.handleAuthErrorAction(
        errorDetails,
        clearAuthState,
        redirectToLogin
      );
    }
  }, [clearAuthState, redirectToLogin]);

  // Clear auth error details
  const clearAuthErrorDetails = () => {
    setAuthErrorDetails(null);
  };

  // Attempt storage recovery - nuclear option that clears everything
  // Called from StorageRecoveryDialog when user clicks "Clear All & Retry"
  const attemptStorageRecovery = useCallback(async (): Promise<boolean> => {
    authLogger.warn('🔧 User initiated nuclear storage recovery...');
    setLoading(true);

    try {
      const success = await clearAllStorage();

      if (success) {
        authLogger.info('✅ Nuclear storage recovery succeeded, redirecting to login');
        setStorageCorrupted(false);
        setSession(null);
        setUser(null);
        // Redirect to login after successful recovery
        window.location.href = '/login';
        return true;
      } else {
        authLogger.error('❌ Nuclear storage recovery failed');
        return false;
      }
    } catch (_error) {
      authLogger.error('❌ Nuclear storage recovery threw exception', _error);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Proactive token refresh (for use after user profile mutations)
  const refreshToken = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    authLogger.debug('Proactive token refresh requested');

    try {
      const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError) {
        authLogger.error('Token refresh failed', refreshError);
        return { success: false, error: refreshError.message };
      }

      if (!refreshedSession) {
        authLogger.error('Token refresh returned no session');
        return { success: false, error: 'No session returned from refresh' };
      }

      authLogger.debug('✅ Token refreshed successfully');
      setSession(refreshedSession);
      setUser(refreshedSession.user);

      return { success: true };
    } catch (error) {
      authLogger.error('Token refresh threw exception', error);
      return { success: false, error: (error as Error).message };
    }
  }, []);

  // Mutex-protected refresh to prevent race conditions with global coordination
  const performSafeRefresh = useCallback(async (): Promise<boolean> => {
    // Check if global refresh is already in progress (from auth-utils)
    const globalRefresh = getGlobalRefreshPromise();
    if (globalRefresh) {
      authLogger.debug('[Auth] Global refresh in progress, waiting...');
      return await globalRefresh;
    }

    // If local refresh already in progress, wait for it
    if (refreshMutex.current) {
      authLogger.debug('[Auth] Local refresh in progress, waiting...');
      await refreshMutex.current;
      // After waiting, we don't know the result, so return true (optimistic)
      return true;
    }

    // Lock and refresh (coordinate both local and global mutexes)
    const refreshOperation = (async (): Promise<boolean> => {
      try {
        authLogger.debug('[Auth] Starting globally-coordinated refresh...');
        const { data, error } = await supabase.auth.refreshSession();
        if (error) {
          authLogger.error('[Auth] Refresh failed:', error);
          return false;
        } else if (data.session) {
          authLogger.debug('[Auth] Session refreshed successfully');
          setSession(data.session);
          setUser(data.session.user);
          return true;
        }
        return false;
      } finally {
        refreshMutex.current = null;
        setGlobalRefreshPromise(null);
      }
    })();

    // Create void wrapper for local mutex compatibility
    const voidRefreshOperation = refreshOperation.then(() => {});

    // Set both local and global mutexes (with appropriate types)
    refreshMutex.current = voidRefreshOperation;
    setGlobalRefreshPromise(refreshOperation);

    return await refreshOperation;
  }, []);

  // Wrapper for performSafeRefresh with timeout protection
  const performSafeRefreshWithTimeout = useCallback(async (): Promise<boolean> => {
    const REFRESH_TIMEOUT = 10000; // 10 seconds

    try {
      const refreshPromise = performSafeRefresh();
      const timeoutPromise = new Promise<boolean>((_, reject) =>
        setTimeout(() => reject(new Error('Token refresh timeout')), REFRESH_TIMEOUT)
      );

      return await Promise.race([refreshPromise, timeoutPromise]);
    } catch (error) {
      if ((error as Error).message === 'Token refresh timeout') {
        authLogger.error('[Auth] Token refresh timed out after 10 seconds');

        // Set error details for user-facing message
        setAuthErrorDetails({
          type: 'network_error',
          originalError: error,
          shouldRedirectToLogin: false,
          shouldClearSession: false,
          userMessage: 'Unable to refresh your session. Please check your connection and try again.'
        });

        return false;
      }

      // Other errors - rethrow
      throw error;
    }
  }, [performSafeRefresh]);

  // Initialize auth state and set up listener
  useEffect(() => {
    setLoading(true);

    const getInitialSession = async () => {
      // CRITICAL FIX: Increased timeout for mobile browsers
      // Maximum initialization timeout - force completion after 20 seconds (was 15s)
      const maxInitTimeout = setTimeout(() => {
        authLogger.error('❌ Auth initialization exceeded 20 second timeout - forcing completion');
        setSession(null);
        setUser(null);
        setLoading(false);
      }, 20000);

      try {
        authLogger.debug('🔄 Initializing auth state...');

        // STORAGE HEALTH CHECK: Detect and recover from corrupted storage BEFORE auth operations
        // This prevents the 15-second timeout hang when IndexedDB is corrupted
        const storageHealth = await checkStorageHealth();

        if (storageHealth.needsRecovery) {
          authLogger.warn('🔧 Storage issue detected, attempting silent recovery...', storageHealth.diagnostics);

          // Attempt targeted recovery (preserves translations)
          const recovered = await clearAuthStorage();

          if (!recovered) {
            // Silent recovery failed - show recovery UI to user
            authLogger.error('❌ Silent storage recovery failed, will show recovery UI');
            setStorageCorrupted(true);
            setLoading(false);
            clearTimeout(maxInitTimeout);
            return;
          }

          authLogger.info('✅ Storage recovered silently, continuing auth init (user will need to re-login)');
          // After recovery, there's no session - user will be redirected to login
          setSession(null);
          setUser(null);
          setLoading(false);
          clearTimeout(maxInitTimeout);
          return;
        }

        // Check if we're in password recovery mode
        const urlHash = window.location.hash;
        const urlSearch = window.location.search;
        const isRecoveryFlow = urlHash.includes('type=recovery') ||
                              urlSearch.includes('type=recovery') ||
                              urlHash.includes('type=invite') ||
                              window.location.pathname.includes('/password-reset');

        // CRITICAL FIX: Increased timeout for mobile storage (10s → 15s)
        // Mobile browsers can take 2-10s to access storage after sleep
        const { data: { session: initialSession } } = await withTimeout(
          supabase.auth.getSession(),
          15000,
          'getSession'
        );

        // Enhanced session validation
        if (initialSession) {
          try {
            const tokenParts = initialSession.access_token.split('.');
            if (tokenParts.length < 3) throw new Error('Invalid JWT format');
            const payload = JSON.parse(atob(tokenParts[1] || '')) as { exp?: number; sub?: string };
            const now = Math.floor(Date.now() / 1000);

            // DISABLED: Proactive session refresh causes race conditions with concurrent API calls
            // When navigating to pages with multiple simultaneous API calls (like ProjectDetailPage),
            // this refresh invalidates the JWT server-side while edge functions are still using the old token
            /*
            if (payload.exp && payload.exp < now + 300) {
              authLogger.warn('Token expiring soon, attempting refresh');
              try {
                const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
                if (refreshData.session && !refreshError) {
                  authLogger.warn('Session refreshed successfully');
                  initialSession = refreshData.session;
                } else {
                  authLogger.warn('Session refresh failed', refreshError);
                }
              } catch (refreshError) {
                authLogger.error('Session refresh threw error', refreshError);
              }
            }
            */

            // Log token status for debugging but don't refresh proactively
            if (payload.exp) {
              const timeUntilExpiry = payload.exp - now;
              authLogger.debug(`🎫 [AUTH-CONTEXT] Token status`, {
                timeUntilExpiry,
                expiresAt: new Date(payload.exp * 1000).toISOString(),
                sub: payload.sub,
                jwt: initialSession?.access_token?.substring(0, 20) + '...',
                autoRefreshEnabled: true, // From supabaseClient config
                timestamp: Date.now()
              });
            }
          } catch (jwtError) {
            authLogger.warn('JWT validation failed', jwtError);
          }
        }

        if (isRecoveryFlow && initialSession) {
          authLogger.debug('🔄 Password recovery flow detected');
          setIsPasswordRecovery(true);
          setSession(null);
          setUser(null);
        } else {
          authLogger.debug('🔄 Normal auth flow');
          setIsPasswordRecovery(false);

          // Set session state directly - let Supabase-js handle auto-refresh
          // CRITICAL FIX: Removed proactive refresh to eliminate race condition
          // Supabase-js will automatically refresh tokens when needed via autoRefreshToken: true
          if (initialSession) {
            authLogger.debug('✅ Using cached session - Supabase will auto-refresh if needed');

            // Mark session fresh BEFORE setSession triggers re-renders and edge function calls.
            // This prevents ensureValidSession() from calling getSession() (which would queue
            // behind navigator.locks and time out if _recoverAndRefresh is still running).
            markSessionFresh();

            setSession(initialSession);
            setUser(initialSession.user);

            // Load language preferences in background (non-blocking)
            if (initialSession.access_token) {
              loadUserLanguageAfterLogin(initialSession.access_token)
                .then(result => {
                  if (!result.error) {
                    const detectedLanguage = detectUserLanguage(
                      result.language,
                      result.companyDefaultLanguage
                    );
                    changeLanguage(detectedLanguage, false)
                      .then(changeResult => {
                        if (changeResult.success) {
                          authLogger.debug('Applied user language preference:', detectedLanguage);
                        } else {
                          authLogger.warn('Failed to apply language preference:', changeResult.error);
                        }
                      })
                      .catch(_changeError => {
                        authLogger.error('Language change failed', _changeError);
                      });
                  }
                })
                .catch(_error => {
                  authLogger.warn('User language loading failed (non-critical)');
                });
            }
          } else {
            // No initial session
            setSession(null);
            setUser(null);
          }
        }
      } catch (_error) {
        const error = _error as Error;
        if (error.message?.includes('timed out')) {
          authLogger.error('❌ Auth initialization timed out', error);
          // CRITICAL: Do NOT call signOut() when storage is broken
          // If getSession() timed out, signOut() will also hang on storage access
          // Just clear local state and skip storage cleanup
          authLogger.warn('⏩ Skipping signOut() - storage appears broken (timeout detected)');
          setSession(null);
          setUser(null);
          setIsPasswordRecovery(false);
        } else {
          authLogger.error('Initial session loading failed', error);
          handleAuthError(error);
          setSession(null);
          setUser(null);
          setIsPasswordRecovery(false);
        }
      } finally {
        clearTimeout(maxInitTimeout);
        setLoading(false);
      }
    };

    getInitialSession();

    // ENTERPRISE AUTH EVENT HANDLER - Single source of truth
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        authLogger.debug(`🔄 Auth state change: ${event}`, {
          event,
          hasNewSession: !!newSession,
          userEmail: newSession?.user?.email,
          timestamp: new Date().toISOString()
        });

        try {
          // Handle each event type explicitly with proper state management
          switch (event) {
            case 'SIGNED_IN': {
              authLogger.debug('✅ User signed in successfully');

              // DISABLED: Edge function logging can hang during auth flows (invitation/password reset)
              // Components should handle their own logging after successful operations
              // if (newSession?.user) {
              //   await authEventLogger.logLoginSuccess(
              //     newSession.user,
              //     newSession,
              //     crypto.randomUUID()
              //   );
              // }

              // Check if this is a password recovery event
              const urlHash = window.location.hash;
              const urlSearch = window.location.search;
              const isCurrentlyRecovery = urlHash.includes('type=recovery') ||
                                         urlSearch.includes('type=recovery') ||
                                         window.location.pathname.includes('/password-reset');

              if (isPasswordRecovery || isCurrentlyRecovery) {
                authLogger.debug('🔄 Handling password recovery sign-in');
                setIsPasswordRecovery(false);

                // DISABLED: All edge function calls during auth events can hang the flow
                // Let the password reset component handle logging after successful submission
                // // Log password reset completion
                // if (newSession?.user) {
                //   await authEventLogger.logEvent({
                //     eventType: 'password_reset',
                //     eventSubtype: 'success',
                //     user: newSession.user,
                //     session: newSession,
                //     correlationId: crypto.randomUUID(),
                //     details: {
                //       resetMethod: 'email_link',
                //       recoveryFlow: true
                //     }
                //   });
                // }

                // // Activate user on first login after password recovery
                // if (newSession?.user?.id) {
                //   try {
                //     const response = await fetch(`${import.meta.env['VITE_PUBLIC_SUPABASE_URL']}/functions/v1/activate-user-first-login`, {
                //       method: 'POST',
                //       headers: {
                //         'Content-Type': 'application/json',
                //         'Authorization': `Bearer ${newSession.access_token}`
                //       },
                //       body: JSON.stringify({ user_id: newSession.user.id })
                //     });

                //     if (!response.ok) {
                //       authLogger.warn('User activation failed', { status: response.status });
                //       await authEventLogger.logEvent({
                //         eventType: 'user_activation',
                //         eventSubtype: 'failed',
                //         user: newSession.user,
                //         errorCode: `HTTP_${response.status}`,
                //         errorCategory: 'system',
                //         correlationId: crypto.randomUUID(),
                //         details: {
                //           httpStatus: response.status,
                //           activationMethod: 'first_login'
                //         }
                //       });
                //     } else {
                //       await authEventLogger.logEvent({
                //         eventType: 'user_activation',
                //         eventSubtype: 'success',
                //         user: newSession.user,
                //         correlationId: crypto.randomUUID(),
                //         details: {
                //           activationMethod: 'first_login',
                //           triggerEvent: 'password_recovery'
                //         }
                //       });
                //     }
                //   } catch (_error) {
                //     authLogger.error('User activation failed', _error);
                //     await authEventLogger.logEvent({
                //       eventType: 'user_activation',
                //       eventSubtype: 'failed',
                //       user: newSession.user,
                //       errorCode: 'ACTIVATION_ERROR',
                //       errorCategory: 'system',
                //       correlationId: crypto.randomUUID(),
                //       details: {
                //         errorMessage: (_error as Error).message,
                //         activationMethod: 'first_login'
                //       }
                //     });
                //   }
                // }
              }

              // CRITICAL: Mark session fresh BEFORE any edge function calls.
              // After signInWithPassword, the session is brand new — no need to re-validate
              // via getSession() (which would queue behind navigator.locks and time out).
              markSessionFresh();
              lastSignedInTime.current = Date.now();

              // Load user language preferences after successful login
              if (newSession?.access_token) {
                loadUserLanguageAfterLogin(newSession.access_token)
                  .then(result => {
                    if (result.error) {
                      authLogger.warn('User language loading failed');
                    } else {
                      // Apply loaded language preference
                      const detectedLanguage = detectUserLanguage(
                        result.language,
                        result.companyDefaultLanguage
                      );
                      changeLanguage(detectedLanguage, false)
                        .then(changeResult => {
                          if (changeResult.success) {
                            authLogger.debug('Applied user language preference after login:', detectedLanguage);
                          } else {
                            authLogger.warn('Failed to apply language preference after login:', changeResult.error);
                          }
                        })
                        .catch(_changeError => {
                          authLogger.error('Language change failed after login', _changeError);
                        });
                    }
                  })
                  .catch(_error => {
                    authLogger.error('User language loading failed', _error);
                  });
              }

              // Set authenticated state
              setSession(newSession);
              setUser(newSession?.user ?? null);
              setLoading(false);
              break;
            }

            case 'SIGNED_OUT':
              authLogger.debug('🔄 User signed out event received', {
                isUserInitiated: isUserInitiatedSignOut.current,
                timestamp: new Date().toISOString()
              });

              // Check if this is a spurious SIGNED_OUT (e.g. from _recoverAndRefresh failing
              // while signInWithPassword is in progress).
              // CRITICAL: Do NOT call getSession() here — it goes through navigator.locks and
              // can deadlock if this event was fired from within _recoverAndRefresh (which holds
              // the lock). Instead, use a simple timestamp check.
              if (!isUserInitiatedSignOut.current) {
                const timeSinceLastSignIn = Date.now() - lastSignedInTime.current;
                if (timeSinceLastSignIn < 10000) {
                  authLogger.warn('🚫 Ignoring SIGNED_OUT: SIGNED_IN was processed <10s ago (likely _recoverAndRefresh interference)', {
                    timeSinceLastSignIn,
                    timestamp: new Date().toISOString()
                  });
                  return; // Don't clear state — the SIGNED_IN session is still valid
                }
              } else {
                authLogger.debug('✅ User-initiated sign-out, proceeding with logout');
              }

              // Legitimate sign-out: clear state
              authLogger.debug('🔄 Processing legitimate SIGNED_OUT - clearing auth state');
              setSession(null);
              setUser(null);
              setLoading(false);
              isUserInitiatedSignOut.current = false; // Reset flag
              break;

            case 'PASSWORD_RECOVERY':
              authLogger.debug('🔄 Password recovery event');
              setIsPasswordRecovery(true);
              setLoading(false);
              break;

            case 'TOKEN_REFRESHED':
              authLogger.debug('🔄 Token refreshed', {
                newSessionExists: !!newSession,
                newAccessToken: newSession?.access_token?.substring(0, 20) + '...',
                newUserId: newSession?.user?.id,
                timestamp: Date.now()
              });

              if (newSession) {
                setSession(newSession);
                setUser(newSession.user);
              }
              break;

            default:
              authLogger.debug(`🔄 Unhandled auth event: ${event}`);
              // For any other events, update session if provided
              if (newSession) {
                setSession(newSession);
                setUser(newSession.user);
              }
              setLoading(false);
              break;
          }
        } catch (error) {
          authLogger.error('Error handling auth state change', error);
          handleAuthError(error);
        }
      }
    );

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [handleAuthError, isPasswordRecovery]); // Removed user - causes circular dependency with SIGNED_OUT handler

  // Proactive token refresh with race condition protection
  useEffect(() => {
    if (!session) return; // No session to refresh

    const checkAndRefresh = async () => {
      // Prevent refresh spam - minimum 1 minute between checks
      const now = Date.now();
      if (now - lastRefreshCheck.current < 60000) {
        authLogger.debug('[Auth] Skipping refresh check - too soon');
        return;
      }
      lastRefreshCheck.current = now;

      try {
        // Get current session
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession) return;

        // Parse JWT to check expiry
        const tokenParts = currentSession.access_token.split('.');
        if (tokenParts.length < 3 || !tokenParts[1]) return;
        const payload = JSON.parse(atob(tokenParts[1]));
        const expiresAt = payload.exp;
        const nowInSeconds = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = expiresAt - nowInSeconds;

        authLogger.debug(`[Auth] Token expires in ${Math.floor(timeUntilExpiry / 60)} minutes`);

        // Check if token is ALREADY expired
        if (timeUntilExpiry < 0) {
          authLogger.warn('[Auth] Token has EXPIRED, forcing immediate refresh', {
            expiredSeconds: Math.abs(timeUntilExpiry),
            expiredAt: new Date(expiresAt * 1000).toISOString()
          });

          try {
            const refreshSuccess = await performSafeRefreshWithTimeout();
            if (!refreshSuccess) {
              // Refresh failed - clear state and redirect to login
              authLogger.error('[Auth] Token refresh failed for expired token, redirecting to login');
              await clearAuthState();
              redirectToLogin();
            } else {
              authLogger.warn('[Auth] ✅ Successfully refreshed expired token');
            }
          } catch (error) {
            authLogger.error('[Auth] Token refresh threw error for expired token', error);
            await clearAuthState();
            redirectToLogin();
          }
          return; // Exit early after handling expired token
        }

        // Refresh if less than 20 minutes remaining (proactive buffer)
        // This ensures refresh happens well before expiry
        if (timeUntilExpiry < 1200 && timeUntilExpiry > 0) {
          authLogger.warn('[Auth] Token expiring soon (<20 min), initiating proactive refresh...', {
            minutesRemaining: Math.floor(timeUntilExpiry / 60)
          });
          await performSafeRefresh();
        }
      } catch (error) {
        authLogger.error('[Auth] Error checking token expiry:', error);
      }
    };

    // Check on tab visibility change (user returns to tab)
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        authLogger.debug('[Auth] Tab became visible, checking token...');

        // Calculate idle time since last refresh check
        const now = Date.now();
        const idleTime = now - lastRefreshCheck.current;

        // If idle >30 minutes, force refresh immediately regardless of token expiry
        if (idleTime > 30 * 60 * 1000) {
          authLogger.warn('[Auth] Extended idle detected (>30 min), forcing immediate token refresh', {
            idleTimeMinutes: Math.floor(idleTime / 60000),
            lastCheck: new Date(lastRefreshCheck.current).toISOString()
          });
          await performSafeRefreshWithTimeout();
        } else {
          // Normal threshold-based refresh check
          checkAndRefresh();
        }
      }
    };

    // Check on window focus (user clicks on window)
    const handleFocus = async () => {
      authLogger.debug('[Auth] Window focused, checking token...');

      // Calculate idle time since last refresh check
      const now = Date.now();
      const idleTime = now - lastRefreshCheck.current;

      // If idle >30 minutes, force refresh immediately
      if (idleTime > 30 * 60 * 1000) {
        authLogger.warn('[Auth] Extended idle detected on focus (>30 min), forcing immediate token refresh', {
          idleTimeMinutes: Math.floor(idleTime / 60000),
          lastCheck: new Date(lastRefreshCheck.current).toISOString()
        });
        await performSafeRefreshWithTimeout();
      } else {
        // Normal threshold-based refresh check
        checkAndRefresh();
      }
    };

    // Periodic check every 5 minutes for proactive token monitoring
    // This ensures we catch expiring tokens even without user interaction
    const interval = setInterval(() => {
      authLogger.debug('[Auth] Periodic token check (5-min interval)...');
      checkAndRefresh();
    }, 5 * 60 * 1000); // 5 minutes (proactive monitoring)

    // ACTIVITY TRACKING: Refresh session every 3 minutes to update session.refreshed_at
    // This keeps the "Real-time activity" dashboard accurate for admin monitoring
    const activityInterval = setInterval(async () => {
      if (!session) return;

      // Only track activity when tab is visible (user is actively using the app)
      if (document.hidden) {
        authLogger.debug('[Activity] Skipping activity tracking - tab not visible');
        return;
      }

      try {
        authLogger.debug('[Activity] Updating user activity timestamp via session refresh');
        const { error } = await supabase.auth.refreshSession();

        if (error) {
          authLogger.warn('[Activity] Activity tracking refresh failed (non-critical)', { error: error.message });
        } else {
          authLogger.debug('[Activity] Activity timestamp updated successfully');
        }
      } catch (_error) {
        authLogger.warn('[Activity] Activity tracking refresh threw error (non-critical)', _error);
      }
    }, 3 * 60 * 1000); // 3 minutes

    // Delay initial check to avoid adding getSession() calls right after login.
    // The session was just validated (by init or SIGNED_IN), so checking immediately is wasteful
    // and risks queuing behind navigator.locks if _recoverAndRefresh is still running.
    const initialCheckTimeout = setTimeout(() => checkAndRefresh(), 30000);

    // CRITICAL FIX: Add mobile browser lifecycle handlers
    // Mobile browsers use bfcache (back-forward cache) that bypasses normal lifecycle

    // Handle page restored from bfcache (browser back/forward)
    const handlePageShow = (event: Event) => {
      // Check if page was restored from bfcache
      const pageTransitionEvent = event as { persisted?: boolean };
      if (pageTransitionEvent.persisted) {
        // Page was restored from bfcache - validate session
        authLogger.warn('[Auth] Page restored from bfcache, validating session...');
        checkAndRefresh();
      }
    };

    // Handle browser suspending tab (mobile sleep)
    const handleFreeze = () => {
      authLogger.debug('[Auth] Browser suspending tab (freeze event)');
      // Save last check time for resume detection
      lastRefreshCheck.current = Date.now();
    };

    // Handle browser resuming tab (mobile wake)
    const handleResume = async () => {
      const now = Date.now();
      const idleTime = now - lastRefreshCheck.current;

      authLogger.warn('[Auth] Browser resumed tab (resume event)', {
        idleTimeMinutes: Math.floor(idleTime / 60000)
      });

      // If idle >30 minutes during freeze, force refresh
      if (idleTime > 30 * 60 * 1000) {
        authLogger.warn('[Auth] Extended idle during freeze detected, forcing token refresh');
        await performSafeRefreshWithTimeout();
      } else {
        checkAndRefresh();
      }
    };

    // Set up event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('freeze', handleFreeze);
    document.addEventListener('resume', handleResume);

    // Cleanup
    return () => {
      clearTimeout(initialCheckTimeout);
      clearInterval(interval);
      clearInterval(activityInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('freeze', handleFreeze);
      document.removeEventListener('resume', handleResume);
    };
  }, [session, performSafeRefresh, performSafeRefreshWithTimeout, clearAuthState, redirectToLogin]); // Removed user dependency - it's updated inside performSafeRefresh, causing circular dependency

  // SignOut function with proper state management
  // CRITICAL: Clear localStorage FIRST, then attempt server-side revocation.
  // supabase.auth.signOut() holds the navigator lock during its API call.
  // If we clear local state first, even if signOut() hangs, the user is
  // effectively logged out and won't see a broken state.
  const handleSignOut = async () => {
    if (loading) {
      authLogger.warn('Logout already in progress, skipping');
      return;
    }

    setLoading(true);

    try {
      // Clear permission-related session storage
      sessionStorage.removeItem('permission_security_key');

      // Mark as user-initiated to prevent spurious event detection
      isUserInitiatedSignOut.current = true;

      // Step 1: Clear local state IMMEDIATELY (no lock needed)
      const supabaseUrl = import.meta.env['VITE_PUBLIC_SUPABASE_URL'] as string | undefined;
      const projectRef = supabaseUrl?.split('//')[1]?.split('.')[0];
      if (projectRef) {
        const storageKey = `sb-${projectRef}-auth-token`;
        localStorage.removeItem(storageKey);
        localStorage.removeItem(`${storageKey}-code-verifier`);
      }
      setSession(null);
      setUser(null);
      authLogger.debug('Local auth state cleared');

      // Step 2: Attempt server-side token revocation (fire-and-forget)
      // This uses signOut() which acquires the lock + network call,
      // but since we already cleared local state, it's not blocking the user.
      supabase.auth.signOut().catch((_error) => {
        authLogger.warn('Server-side signOut failed (non-critical, token will expire)', _error);
      });
    } catch (error: unknown) {
      authLogger.error('Logout failed', error);
      setSession(null);
      setUser(null);
    } finally {
      setLoading(false);
      isUserInitiatedSignOut.current = false;
    }
  };

  const value = {
    session,
    user,
    loading,
    signOut: handleSignOut,
    isPasswordRecovery,
    handleAuthError,
    authErrorDetails,
    clearAuthErrorDetails,
    refreshToken,
    storageCorrupted,
    attemptStorageRecovery,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};